import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Sparkles, Check, X, ChevronRight, Clock, User,
  ListTodo, Target, AlertCircle, Newspaper, BarChart3,
  Loader2, Zap, ThumbsUp, ThumbsDown, Edit2, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Suggestion type configurations
const SUGGESTION_TYPES = {
  todo: { icon: ListTodo, label: 'To-Do', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  rock: { icon: Target, label: 'Rock', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  issue: { icon: AlertCircle, label: 'Issue', color: 'text-red-400', bg: 'bg-red-500/20' },
  headline: { icon: Newspaper, label: 'Headline', color: 'text-green-400', bg: 'bg-green-500/20' },
  scorecard: { icon: BarChart3, label: 'Scorecard', color: 'text-yellow-400', bg: 'bg-yellow-500/20' }
};

// Confidence badge styling
const getConfidenceBadge = (confidence, level) => {
  if (level === 'high' || confidence >= 0.9) {
    return { text: 'High', className: 'bg-green-500/20 text-green-400 border-green-500/50' };
  } else if (level === 'medium' || confidence >= 0.75) {
    return { text: 'Medium', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' };
  }
  return { text: 'Low', className: 'bg-red-500/20 text-red-400 border-red-500/50' };
};

/**
 * Single Suggestion Card Component
 */
const SuggestionCard = ({ suggestion, onApprove, onReject, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const typeConfig = SUGGESTION_TYPES[suggestion.type] || SUGGESTION_TYPES.todo;
  const TypeIcon = typeConfig.icon;
  const confidenceBadge = getConfidenceBadge(suggestion.confidence, suggestion.confidence_level);
  
  return (
    <div className={`
      rounded-lg border border-slate-700 overflow-hidden transition-all
      ${isExpanded ? 'bg-slate-800/50' : 'bg-slate-800/30 hover:bg-slate-800/50'}
    `}>
      {/* Header */}
      <div 
        className="p-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-3">
          {/* Type icon */}
          <div className={`p-1.5 rounded ${typeConfig.bg}`}>
            <TypeIcon className={`h-4 w-4 ${typeConfig.color}`} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-[10px] px-1.5">
                {typeConfig.label}
              </Badge>
              <Badge variant="outline" className={`text-[10px] px-1.5 ${confidenceBadge.className}`}>
                {Math.round(suggestion.confidence * 100)}% {confidenceBadge.text}
              </Badge>
            </div>
            <h4 className="text-sm font-medium text-white truncate">
              {suggestion.title}
            </h4>
            {suggestion.suggested_owner_name && (
              <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                <User className="h-3 w-3" />
                {suggestion.suggested_owner_name}
              </div>
            )}
          </div>
          
          {/* Expand indicator */}
          <ChevronRight className={`h-4 w-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </div>
      </div>
      
      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-slate-700/50">
          {/* Description */}
          {suggestion.description && (
            <p className="text-sm text-slate-300 pt-3">
              {suggestion.description}
            </p>
          )}
          
          {/* Evidence */}
          {suggestion.evidence?.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs text-slate-500">Evidence:</span>
              <ul className="text-xs text-slate-400 space-y-1">
                {suggestion.evidence.map((e, idx) => (
                  <li key={idx} className="flex items-start gap-1">
                    <span className="text-slate-600">•</span>
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Source */}
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Clock className="h-3 w-3" />
            From {suggestion.source_section} section
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <Button
              size="sm"
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={(e) => { e.stopPropagation(); onApprove(suggestion); }}
            >
              <ThumbsUp className="h-3.5 w-3.5 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={(e) => { e.stopPropagation(); onEdit(suggestion); }}
            >
              <Edit2 className="h-3.5 w-3.5 mr-1" />
              Edit
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="flex-1"
              onClick={(e) => { e.stopPropagation(); onReject(suggestion); }}
            >
              <ThumbsDown className="h-3.5 w-3.5 mr-1" />
              Reject
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Edit Suggestion Dialog
 */
const EditSuggestionDialog = ({ suggestion, open, onOpenChange, onSave }) => {
  // Initialize state directly from prop - only update when dialog opens
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Reset form when suggestion changes (dialog opens with new suggestion)
  const suggestionId = suggestion?.id;
  const suggestionTitle = suggestion?.title || '';
  const suggestionDescription = suggestion?.description || '';
  
  // Use layoutEffect to sync state before paint when suggestion changes
  React.useLayoutEffect(() => {
    if (suggestionId) {
      setTitle(suggestionTitle);
      setDescription(suggestionDescription);
    }
  }, [suggestionId, suggestionTitle, suggestionDescription]);
  
  const handleSave = async () => {
    setSaving(true);
    await onSave({ title, description });
    setSaving(false);
  };
  
  if (!suggestion) return null;
  
  const typeConfig = SUGGESTION_TYPES[suggestion.type] || SUGGESTION_TYPES.todo;
  const TypeIcon = typeConfig.icon;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-slate-900 border-slate-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className={`p-1.5 rounded ${typeConfig.bg}`}>
              <TypeIcon className={`h-4 w-4 ${typeConfig.color}`} />
            </div>
            Edit & Approve {typeConfig.label}
          </DialogTitle>
          <DialogDescription>
            Modify the suggestion before applying it.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full h-24 px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-sm resize-none"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="bg-green-600 hover:bg-green-700"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            Approve & Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Suggestion Queue Component
 * Displays pending AI suggestions for review and approval
 */
const SuggestionQueue = ({
  meetingId,
  autoApplyMode = 'disabled',
  onSuggestionApplied,
  className = ""
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [editingSuggestion, setEditingSuggestion] = useState(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, auto_applied: 0 });
  
  // Load suggestions
  const loadSuggestions = useCallback(async () => {
    if (!meetingId) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/eos/meetings/${meetingId}/suggestions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuggestions(response.data.suggestions || []);
      setStats({
        pending: response.data.by_status?.pending?.length || 0,
        approved: response.data.by_status?.approved?.length || 0,
        rejected: response.data.by_status?.rejected?.length || 0,
        auto_applied: response.data.by_status?.auto_applied?.length || 0
      });
      
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);
  
  useEffect(() => {
    loadSuggestions();
    
    // Poll for new suggestions every 30 seconds
    const interval = setInterval(loadSuggestions, 30000);
    return () => clearInterval(interval);
  }, [loadSuggestions]);
  
  // Approve suggestion
  const handleApprove = async (suggestion, modifications = null) => {
    setProcessing(suggestion.id);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/eos/meetings/${meetingId}/suggestions/${suggestion.id}`,
        { action: 'approve', modifications },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`${SUGGESTION_TYPES[suggestion.type]?.label || 'Item'} created`);
        onSuggestionApplied?.(suggestion, response.data.entity_id);
        loadSuggestions();
      } else {
        toast.error(response.data.error || 'Failed to apply suggestion');
      }
      
    } catch (error) {
      console.error('Error approving suggestion:', error);
      toast.error('Failed to approve suggestion');
    } finally {
      setProcessing(null);
    }
  };
  
  // Reject suggestion
  const handleReject = async (suggestion, reason = null) => {
    setProcessing(suggestion.id);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/eos/meetings/${meetingId}/suggestions/${suggestion.id}`,
        { action: 'reject', reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.info('Suggestion dismissed');
      loadSuggestions();
      
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
      toast.error('Failed to dismiss suggestion');
    } finally {
      setProcessing(null);
    }
  };
  
  // Handle edit and save
  const handleEditSave = async (modifications) => {
    if (editingSuggestion) {
      await handleApprove(editingSuggestion, modifications);
      setEditingSuggestion(null);
    }
  };
  
  // Bulk approve high confidence
  const handleBulkApprove = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/eos/meetings/${meetingId}/suggestions/bulk-approve`,
        { min_confidence: 0.9 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.processed} suggestions approved`);
      loadSuggestions();
      
    } catch (error) {
      console.error('Error bulk approving:', error);
      toast.error('Failed to bulk approve');
    }
  };
  
  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
  
  return (
    <>
      <Card className={`bg-slate-900 border-slate-800 ${className}`}>
        <CardHeader className="py-3 px-4 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-400" />
              AI Suggestions
              {pendingSuggestions.length > 0 && (
                <Badge className="bg-purple-600 text-white text-[10px]">
                  {pendingSuggestions.length} pending
                </Badge>
              )}
            </CardTitle>
            
            {autoApplyMode !== 'disabled' && (
              <Badge variant="outline" className="text-[10px] border-yellow-500 text-yellow-400">
                <Zap className="h-2.5 w-2.5 mr-1" />
                Auto-Apply: {autoApplyMode.replace('_', ' ')}
              </Badge>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
            </div>
          ) : pendingSuggestions.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Eye className="h-8 w-8 mx-auto mb-2 text-slate-600" />
              <p className="text-sm text-slate-500">No pending suggestions</p>
              <p className="text-xs text-slate-600 mt-1">
                AI suggestions will appear here during the meeting
              </p>
            </div>
          ) : (
            <>
              {/* Stats bar */}
              <div className="flex items-center gap-4 px-4 py-2 bg-slate-800/50 text-xs">
                <span className="text-slate-500">
                  <span className="text-green-400 font-medium">{stats.approved}</span> approved
                </span>
                <span className="text-slate-500">
                  <span className="text-purple-400 font-medium">{stats.auto_applied}</span> auto-applied
                </span>
                <span className="text-slate-500">
                  <span className="text-red-400 font-medium">{stats.rejected}</span> dismissed
                </span>
                
                {/* Bulk approve button */}
                {pendingSuggestions.some(s => s.confidence >= 0.9) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto h-6 text-[10px]"
                    onClick={handleBulkApprove}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Approve High Confidence
                  </Button>
                )}
              </div>
              
              {/* Suggestions list */}
              <ScrollArea className="h-[300px]">
                <div className="p-3 space-y-2">
                  {pendingSuggestions.map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onEdit={setEditingSuggestion}
                    />
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Edit Dialog */}
      <EditSuggestionDialog
        suggestion={editingSuggestion}
        open={!!editingSuggestion}
        onOpenChange={(open) => !open && setEditingSuggestion(null)}
        onSave={handleEditSave}
      />
    </>
  );
};

export default SuggestionQueue;
